import { useEffect, useRef } from "react";
import { refreshSession } from "../services/sessionRefresh";
import {
  calculateRefreshTime,
  getTokenExpiry,
  isTokenExpired,
  WS_TOKEN_VALIDITY_BUFFER_MS,
} from "../utils/tokenUtils";
import useAuth from "./useAuth";

/**
 * Hook that proactively refreshes access tokens before they expire.
 * Refreshes tokens at 80% of their lifetime to prevent expired token errors.
 * When the tab becomes visible, refreshes immediately if the token is expired or within buffer
 * (avoids WebSocket handshakes with expired tokens after background tab throttling).
 * Only runs when user is authenticated.
 */
export function useProactiveTokenRefresh() {
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { access, isAuthenticated } = useAuth();
  const accessRef = useRef(access);

  accessRef.current = access;

  useEffect(() => {
    // Only run if user is authenticated
    if (!isAuthenticated || !access) {
      // Clear any existing timeout if user logs out
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      return;
    }

    // Calculate when to refresh
    const expiry = getTokenExpiry(access);
    if (!expiry) {
      return;
    }

    const refreshTime = calculateRefreshTime(expiry);
    const delay = Math.max(0, refreshTime - Date.now());

    // Schedule the refresh
    refreshTimeoutRef.current = setTimeout(async () => {
      try {
        // The refresh token is an HttpOnly cookie; refreshSession stores the
        // new access token, and this effect re-runs on it to schedule the next.
        await refreshSession();
      } catch (error) {
        console.error("Proactive token refresh failed:", error);
        // Fall back to reactive refresh on next request
        // The axios interceptor will handle it
      }
    }, delay);

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [access, isAuthenticated]);

  // When tab becomes visible, refresh immediately if token is expired or within buffer
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const currentAccess = accessRef.current;
      if (!currentAccess) return;
      if (!isTokenExpired(currentAccess, WS_TOKEN_VALIDITY_BUFFER_MS)) return;

      refreshSession().catch((error) => {
        console.error("Visibility token refresh failed:", error);
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);
}
